import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PortableText } from "@portabletext/react";
import "./Work.scss";

const CaseStudyModal = ({ isOpen, onClose, work }) => {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!work) return null;

  const title = work.caseStudyTitle || work.title;
  const subtitle = work.caseStudySubtitle || "";
  const highlights = Array.isArray(work.caseStudyHighlights)
    ? work.caseStudyHighlights
    : [];
  const sections = Array.isArray(work.caseStudySections)
    ? work.caseStudySections
    : [];

  const disclaimer =
    work.caseStudyDisclaimer ||
    "Note: Content is summarized to respect enterprise confidentiality and regulatory constraints.";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="app__case-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={onClose}
        >
          <motion.div
            className="app__case-modal"
            initial={{ y: 30, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Case Study Modal"
          >
            <div className="app__case-modal-header">
              <div>
                <h3 className="bold-text">{title}</h3>
                {subtitle ? (
                  <p className="p-text" style={{ marginTop: 6, opacity: 0.85 }}>
                    {subtitle}
                  </p>
                ) : null}
              </div>

              <button
                type="button"
                className="app__case-modal-close"
                onClick={onClose}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {/* Highlights (optional) */}
            {highlights.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <h4 className="bold-text">Highlights</h4>
                <ul className="p-text" style={{ marginTop: 8, paddingLeft: "1.2rem" }}>
                  {highlights.map((h, idx) => (
                    <li key={idx} style={{ margin: "0.35rem 0" }}>
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Sections (preferred) */}
            {sections.length > 0 ? (
              <div style={{ marginTop: 18 }}>
                {sections.map((section, idx) => (
                  <div key={idx} style={{ marginTop: idx === 0 ? 0 : 18 }}>
                    {section?.heading ? (
                      <h4 className="bold-text">{section.heading}</h4>
                    ) : null}

                    {Array.isArray(section?.body) && section.body.length > 0 ? (
                      <div className="p-text" style={{ marginTop: 8 }}>
                        <PortableText value={section.body} />
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              // Fallback if no sections exist yet
              <p className="p-text" style={{ marginTop: 14 }}>
                {work.caseStudySummary || work.description}
              </p>
            )}

            {/* Public Experience link (optional) */}
            {work.projectLink ? (
              <div className="app__case-modal-actions">
                <a href={work.projectLink} target="_blank" rel="noreferrer" className="p-text">
                  View Public Experience →
                </a>
              </div>
            ) : null}

            <p className="p-text" style={{ marginTop: 16, opacity: 0.75 }}>
              {disclaimer}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CaseStudyModal;